// Agricultural Season Management Service for FarmTrackPro
// Handles season management, year-end closing, and crop rotation tracking

class SeasonService {
  constructor() {
    this.currentSeason = this.getCurrentSeason();
  }

  // Get current agricultural season (starts in March, ends in February)
  getCurrentSeason() {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-based (0 = January)
    
    // Agricultural season starts in March (month 2) and ends in February (month 1)
    if (currentMonth >= 2) { // March to December
      return currentYear;
    } else { // January to February (belongs to previous season)
      return currentYear - 1;
    }
  }

  // Get season start and end dates
  getSeasonDates(season) {
    return {
      start: new Date(season, 2, 1), // March 1st
      end: new Date(season + 1, 1, 28) // February 28th (or 29th)
    };
  }

  // Check if we're approaching season end (within 2 months)
  isApproachingSeasonEnd() {
    const now = new Date();
    const currentMonth = now.getMonth();
    
    // Season ends in February, so we're approaching if it's January or February
    return currentMonth === 0 || currentMonth === 1;
  }

  // Get farm seasons data
  getFarmSeasons(farmId) {
    const savedSeasons = localStorage.getItem(`farmSeasons_${farmId}`);
    if (savedSeasons) {
      return JSON.parse(savedSeasons);
    }
    
    // Initialize with current season
    const currentSeason = this.getCurrentSeason();
    const initialSeasons = {
      currentSeason: currentSeason,
      seasons: {
        [currentSeason]: {
          year: currentSeason,
          status: 'active',
          startDate: this.getSeasonDates(currentSeason).start.toISOString(),
          endDate: this.getSeasonDates(currentSeason).end.toISOString(),
          createdAt: new Date().toISOString(),
          data: {
            parcels: [],
            fields: [],
            tasks: [],
            inventory: [],
            finances: {
              revenue: { total: '€0' },
              expenses: { total: '€0' },
              profit: { total: '€0' }
            },
            weather: [],
            notes: ''
          }
        }
      }
    };
    
    this.saveFarmSeasons(farmId, initialSeasons);
    return initialSeasons;
  }

  // Save farm seasons data
  saveFarmSeasons(farmId, seasonsData) {
    localStorage.setItem(`farmSeasons_${farmId}`, JSON.stringify(seasonsData));
  }

  // Get current season data for a farm
  getCurrentSeasonData(farmId) {
    const seasons = this.getFarmSeasons(farmId);
    const currentSeason = seasons.currentSeason;
    return seasons.seasons[currentSeason] || null;
  }

  // Save data to current season
  saveToCurrentSeason(farmId, dataType, data) {
    const seasons = this.getFarmSeasons(farmId);
    const currentSeason = seasons.currentSeason;
    
    if (!seasons.seasons[currentSeason]) {
      seasons.seasons[currentSeason] = this.createEmptySeasonData(currentSeason);
    }
    
    seasons.seasons[currentSeason].data[dataType] = data;
    seasons.seasons[currentSeason].lastUpdated = new Date().toISOString();
    
    this.saveFarmSeasons(farmId, seasons);
  }

  // Create empty season data structure
  createEmptySeasonData(year) {
    return {
      year: year,
      status: 'active',
      startDate: this.getSeasonDates(year).start.toISOString(),
      endDate: this.getSeasonDates(year).end.toISOString(),
      createdAt: new Date().toISOString(),
      data: {
        parcels: [],
        fields: [],
        tasks: [],
        inventory: [],
        finances: {
          revenue: { total: '€0' },
          expenses: { total: '€0' },
          profit: { total: '€0' }
        },
        weather: [],
        notes: ''
      }
    };
  }

  // Close current season and start new one
  async closeSeasonAndStartNew(farmId, closingNotes = '') {
    try {
      const seasons = this.getFarmSeasons(farmId);
      const currentSeason = seasons.currentSeason;
      const newSeason = currentSeason + 1;

      // Close current season
      if (seasons.seasons[currentSeason]) {
        seasons.seasons[currentSeason].status = 'closed';
        seasons.seasons[currentSeason].closedAt = new Date().toISOString();
        seasons.seasons[currentSeason].closingNotes = closingNotes;
      }

      // Create new season
      seasons.seasons[newSeason] = this.createEmptySeasonData(newSeason);
      seasons.currentSeason = newSeason;

      // Copy relevant data from previous season (for crop rotation analysis)
      const previousData = seasons.seasons[currentSeason]?.data;
      if (previousData) {
        // Copy field locations but reset crops for rotation planning
        seasons.seasons[newSeason].data.fields = previousData.fields.map(field => ({
          ...field,
          crop: '',
          cropType: '',
          stage: '',
          previousCrop: field.crop, // Track previous crop for rotation
          previousSeason: currentSeason
        }));

        // Copy equipment inventory (but reset materials)
        seasons.seasons[newSeason].data.inventory = {
          equipments: previousData.inventory.equipments || [],
          materials: []
        };
      }

      this.saveFarmSeasons(farmId, seasons);

      return {
        success: true,
        closedSeason: currentSeason,
        newSeason: newSeason,
        message: `Season ${currentSeason} closed successfully. Welcome to Season ${newSeason}!`
      };

    } catch (error) {
      console.error('Error closing season:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get season comparison data
  getSeasonComparison(farmId, season1, season2) {
    const seasons = this.getFarmSeasons(farmId);
    const data1 = seasons.seasons[season1]?.data;
    const data2 = seasons.seasons[season2]?.data;

    if (!data1 || !data2) {
      return null;
    }

    return {
      season1: season1,
      season2: season2,
      comparison: {
        fields: {
          season1Count: data1.fields.length,
          season2Count: data2.fields.length,
          season1Area: this.calculateTotalArea(data1.fields),
          season2Area: this.calculateTotalArea(data2.fields)
        },
        crops: {
          season1Crops: this.getCropSummary(data1.fields),
          season2Crops: this.getCropSummary(data2.fields)
        },
        finances: {
          season1: data1.finances,
          season2: data2.finances
        },
        tasks: {
          season1Count: data1.tasks.length,
          season2Count: data2.tasks.length,
          season1Completed: data1.tasks.filter(t => t.status === 'completed').length,
          season2Completed: data2.tasks.filter(t => t.status === 'completed').length
        }
      }
    };
  }

  // Calculate total area from fields
  calculateTotalArea(fields) {
    return fields.reduce((total, field) => {
      return total + (parseFloat(field.area) || 0);
    }, 0).toFixed(2);
  }

  // Get crop summary from fields
  getCropSummary(fields) {
    const cropSummary = {};
    fields.forEach(field => {
      if (field.crop) {
        cropSummary[field.crop] = (cropSummary[field.crop] || 0) + (parseFloat(field.area) || 0);
      }
    });
    return cropSummary;
  }

  // Get crop rotation recommendations
  getCropRotationRecommendations(farmId) {
    const seasons = this.getFarmSeasons(farmId);
    const currentSeason = seasons.currentSeason;
    const currentData = seasons.seasons[currentSeason]?.data;

    if (!currentData || !currentData.fields) {
      return [];
    }

    const recommendations = [];

    currentData.fields.forEach(field => {
      if (field.previousCrop) {
        const recommendation = this.getRotationRecommendation(field.previousCrop);
        recommendations.push({
          fieldId: field.id,
          fieldName: field.name,
          previousCrop: field.previousCrop,
          recommendedCrops: recommendation.crops,
          reason: recommendation.reason,
          benefits: recommendation.benefits
        });
      }
    });

    return recommendations;
  }

  // Get rotation recommendation based on previous crop
  getRotationRecommendation(previousCrop) {
    const rotationRules = {
      'wheat': {
        crops: ['corn', 'soybean', 'alfalfa'],
        reason: 'Wheat depletes nitrogen; rotate with nitrogen-fixing or different nutrient requirements',
        benefits: ['Improved soil health', 'Pest management', 'Better yields']
      },
      'corn': {
        crops: ['soybean', 'wheat', 'alfalfa'],
        reason: 'Corn is heavy nitrogen feeder; rotate with nitrogen-fixing legumes',
        benefits: ['Soil nitrogen restoration', 'Disease prevention', 'Improved soil structure']
      },
      'soybean': {
        crops: ['corn', 'wheat'],
        reason: 'Soybeans fix nitrogen; follow with nitrogen-demanding crops',
        benefits: ['Utilize fixed nitrogen', 'Break pest cycles', 'Maximize soil benefits']
      },
      'alfalfa': {
        crops: ['corn', 'wheat'],
        reason: 'Alfalfa improves soil structure; follow with annual crops',
        benefits: ['Excellent soil preparation', 'High nitrogen availability', 'Improved water infiltration']
      },
      'fallow': {
        crops: ['wheat', 'corn', 'soybean'],
        reason: 'Fallow land is ready for any crop with proper soil preparation',
        benefits: ['Soil rest completed', 'Weed control achieved', 'Moisture conservation']
      }
    };

    return rotationRules[previousCrop] || {
      crops: ['wheat', 'corn', 'soybean'],
      reason: 'Standard rotation options',
      benefits: ['Crop diversification', 'Risk management']
    };
  }

  // Get all seasons for a farm
  getAllSeasons(farmId) {
    const seasons = this.getFarmSeasons(farmId);
    return Object.keys(seasons.seasons)
      .map(year => ({
        year: parseInt(year),
        ...seasons.seasons[year]
      }))
      .sort((a, b) => b.year - a.year); // Most recent first
  }

  // Check if season can be closed
  canCloseSeason(farmId) {
    const seasons = this.getFarmSeasons(farmId);
    const currentSeason = seasons.currentSeason;
    const currentSeasonData = seasons.seasons[currentSeason];

    if (!currentSeasonData || currentSeasonData.status === 'closed') {
      return { canClose: false, reason: 'Season already closed' };
    }

    const now = new Date();
    const seasonEnd = new Date(currentSeasonData.endDate);

    // Can close if we're within 1 month of season end or past it
    const oneMonthBeforeEnd = new Date(seasonEnd);
    oneMonthBeforeEnd.setMonth(oneMonthBeforeEnd.getMonth() - 1);

    if (now < oneMonthBeforeEnd) {
      return { 
        canClose: false, 
        reason: `Season can only be closed starting ${oneMonthBeforeEnd.toLocaleDateString('ro-RO')}` 
      };
    }

    return { canClose: true, reason: 'Season can be closed' };
  }
}

export default new SeasonService(); 
 